import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import {
  tierNameFromBand,
  nextTierName,
  cutForTierOnScoreScale,
} from "../lib/creatorTierMetrics";
import { displayOverallScore } from "../lib/tierSatisfaction";
import { tierIndex } from "../lib/creatorTierNames";
import { isMissingColumnError, isMissingFunctionError, isTransientRpcError } from "../lib/dbErrors";
import { ownerLike, sameOwnerEmail } from "../lib/likePattern";
import { pickTierRows } from "../lib/tierRowPick";
import { addTierRowsRevisit, createTierRowsWatch, noteTierAttempt, onTierRowsReset, tierRowsGeneration } from "../lib/tierRowsSignal";

/**
 * useCreatorTierSummary — 등급 요약 최소 조회 훅. 홈 등급 카드 · 상단바 배지 · Settings(내 정보)
 * 등급 카드가 쓴다. `src/pages/MyTier.jsx`는 이력(월별 전부·일별 30일)이 필요해 `fetchMyTierHistory`(이 파일)를 쓴다.
 *
 * 내 등급 행은 **서버 함수 `my_tier_rows` 하나**로 읽는다(2026-10-10 리뷰 60차) — 고르는 규칙(주인 대소문자 무시 · 같은 날·달 두 행이면
 * 나중에 계산된 행 → 같으면 진행도 높은 행 · 87차 #3)은 서버 `creator_tier_daily_per_day`·`creator_tier_monthly_per_month` 한 곳이고, 화면은 표를 직접 읽어 그 규칙을
 * 다시 쓰지 않는다(갈라지면 「카드 진행도 ≠ 서버 판정」). 표 직접 질의 + `pickTierRows` 는 마이그레이션 적용 전 물러나기와 서버 함수의 **일시** 오류(제한 시간·연결) 때의 물러나기 전용(82차 #3 · 83차 #1).
 * 기본 조회는 최신 월 2행 + 최신 일 1행 + 채널 수 + (휴면이면) 마지막 쇼츠 + 그 달의 컷 1행.
 *
 * 2026-09-02 — 대표 확정 「등급 화면 카탈로그」(열세 상태) 배선. 이 훅이 내는 요약을
 * `src/lib/tierCardCopy.js`(`buildTierView`)가 받아 네 화면의 문장을 만든다. 넓힌 것:
 *
 * 1) **등급 행이 없어도 요약을 낸다** (`includeVariants` 일 때). 크리에이터 502명 중 405명이
 *    행이 없어(그중 승인·로그인 96명) 화면 어디에도 등급이 없었다 — "등급 없음"과 "아직 시작 안 함"을
 *    같은 것으로 취급한 탓. 이제 `status: "pre-rookie"` 로 돌려주고, 채널 수(my_tier_rows)로
 *    `hasChannels`(0단계/1단계 구분)를 준다(루키도 · 50·51차).
 * 2) **방향** `direction`: "up" | "down" | "same" | null — 오늘(daily) 등급 vs 적용(monthly) 등급.
 *    8월 실측: 월말 오늘 기준이 아래였던 7명 중 6명이 실제 강등. 대표 결정으로 한 달 내내 문장으로 쓴다.
 * 3) **유지/여유 점수** `gapToKeep`: down이면 적용 등급 컷까지 모자란 점수, up/마스터면 컷 위 여유.
 *    컷 수치 자체는 내보내지 않는다(노출 금지) — 차이만 나간다.
 * 4) **강등·데뷔** `demotedFrom`, `debuted` (승급 `promotedFrom`과 같은 월 2행 비교). 이번 달
 *    강등 7 : 승급 1 인데 화면은 승급만 보여줬다.
 * 5) **마스터 안 순위** `bandRank`, `bandSize` — 테이블에 이미 있는 컬럼.
 * 6) **휴면은 일별 `is_dormant` 우선** — 복귀(영상 1편)가 다음 날 일별 계산에서 바로 반영되게.
 *    월 행의 is_dormant 는 1일까지 안 바뀐다.
 * 7) **마지막 쇼츠 제작일** `lastShortsAt` — 휴면일 때만, 본인 채널의 작품 연결 영상
 *    (`youtube_video_map.licensed_video_id is not null`) `published_at` 최댓값. 대표 결정: 새 컬럼
 *    없이 이 값을 기준으로 한다. 로그인 사용자가 읽을 수 있는 테이블(RLS select=true)이다.
 * 8) 다음 결정일은 요약에 넣지 않는다 — 요약을 만든 때에 굳어 자정 뒤 열어 둔 탭에서 틀린다. 화면이 그리는 때 `nextConfirmDateLabel(now)`(132차 #4 · 옛 `nextConfirmDate`·`daysToConfirm` 삭제).
 *
 * `includeVariants` 기본값 false 는 예전 계약 그대로(루키·휴면·행 없음 → null). 새 필드는
 * 그 계약에서도 함께 채워진다(정식 등급이면).
 *
 * 조회 결과는 **모듈 레벨에서 5분간 캐시**하고 진행 중 요청을 합친다 — 배지와 홈 카드가 같은
 * 데이터를 각자 조회하지 않게.
 *
 * @param {string|undefined} email
 * @param {{ enabled?: boolean, includeVariants?: boolean }} [options]
 * @returns {{ loading: boolean, summary: object|null }}
 */
const ROWS_CACHE_TTL_MS = 5 * 60 * 1000;
// 회원마다 한 칸(email → { at, rows } · email → promise) — 한 칸을 같이 쓰면 두 회원을 그릴 때(대리 접속 창 둘) 서로를 밀어내 탭 복귀마다
// 번갈아 다시 읽었다(103차 #3). 회원 수만큼만 쌓인다(그린 회원) — 오래된 칸은 넣을 때 정리한다
const rowsCache = new Map();
const rowsInflight = new Map();
// 회원마다 마지막으로 서버에 물은 시각 — 실패 뒤 탭 복귀 다시 시도의 간격(91차 #1). 회원마다 두는 까닭: 한 칸이면 다른 회원(대리 접속 전환)을 방금
// 물은 시각이 지금 회원의 「실패 뒤 다시 시도」를 30초 미룬다(102차 #4 · 심사 상태 모듈과 같게)
const rowsLastAttemptAt = new Map();

// 「다시 읽어라」 신호는 src/lib/tierRowsSignal.js 한 곳(93차 #5) — 채널 쓰기 도우미(channelWrites)가 성공하면 보내고(63차 #1 · 91차 #5),
// 이 모듈은 신호 때 캐시를 비우고(진행 중이던 조회도 버린다 — 바뀌기 전에 시작한 조회가 옛 채널 수를 캐시에 다시 쓰지 않게 · 세대 번호 64차 #1),
// 떠 있는 화면(상단 배지·홈 카드)은 신호를 들어 다시 조회한다(65차 #1). 예전 이름은 그대로 내보낸다
export { invalidateTierRows as invalidateTierRowsCache } from "../lib/tierRowsSignal";
onTierRowsReset(() => resetRows());
// 이 탭 밖의 변경(다른 탭·다른 기기·운영팀 SQL·대기열 크론의 자동 종료)은 쓰기 도우미가 닿지 않는다 — 화면으로 돌아왔을 때(탭이 다시 보일 때)
// 다시 읽는다(89차 #2) · 떠 있는 훅이 있을 때만 듣는다(신호 모듈).
//   · 루키 계열(월 확정 행 없음·루키)만, 캐시가 30초보다 오래됐을 때 — 정식 등급 회원의 카드는 다음 달 1일에야 바뀌어 5분 캐시 그대로(90차 #3)
//   · 지난 조회가 실패해 캐시가 비어 있으면 다시 시도(90차 #2) — 단 마지막 시도에서 30초가 지났을 때만(장애 중 탭을 오갈 때마다 부르지 않게 · 91차 #1).
//     한 번도 조회하지 않았으면(요약 훅이 꺼진 화면) 아무것도 하지 않는다 · 진행 중인 조회가 있으면 기다린다
//   · 심사 상태(useRookiePromotionStatus)는 자기 캐시로 따로 판정하고(102차 #5), 내 등급 이력(MyTier)은 이 모듈의 알림을 듣는다(subscribeTierSummary · 90차 #4)
//   · 판정 규칙(30초 · 진행 중 · 실패 뒤)은 신호 모듈 addTierRowsRevisit 한 곳(104차 #5)
// 켜진 훅(회원 이메일별 수) — 탭 복귀 판정은 지금 그 회원을 그리는 훅이 있을 때만 참이다(꺼진 훅·대리 접속을 마친 옛 회원의 캐시가 탭마다 신호를 보내지 않게 · 95차 #1·#2)
const rowsWatch = createTierRowsWatch();
// 이 모듈의 신호 듣기(내 등급 화면의 이력) — 전체 신호와 이 모듈의 탭 복귀 다시 읽기를 듣는다. 심사 상태 캐시만 오래됐을 때는 알리지 않는다(102차 #5)
// email 을 넘기면 탭 복귀 다시 읽기는 그 회원이 오래됐을 때만 받는다(전체 신호는 늘 받는다 · 102차 #4).
// 이름을 신호 모듈의 subscribeTierRows(전체 신호만)와 다르게 둔다 — 같은 이름이면 잘못 가져와도 티가 안 나고 탭 복귀 다시 읽기를 못 받는다(103차 #4)
export const subscribeTierSummary = (listener, email) => rowsWatch.subscribe(listener, email);
const resetRows = () => {
  rowsCache.clear();
  rowsInflight.clear();
  channelInflight = null;
};
// 탭 복귀 규칙은 신호 모듈 한 곳(addTierRowsRevisit · 104차 #5) — 이 모듈은 캐시 읽는 법과 「루키 계열만」만 준다(정식 등급 회원은 5분 캐시 그대로 · 90차 #3)
addTierRowsRevisit(rowsWatch, {
  isInflight: (e) => rowsInflight.has(e),
  cacheAt: (e) => rowsCache.get(e)?.at,
  revisitable: (e) => {
    const monthRow = rowsCache.get(e)?.rows?.monthlyRows?.[0] || null;
    return !monthRow || monthRow.status === "rookie";
  },
  lastAttemptAt: (e) => rowsLastAttemptAt.get(e),
  dropCache: (e) => rowsCache.delete(e),
});

// 테스트·강제 새로고침용
export function _resetTierRowsCache() {
  resetRows();
  rowsLastAttemptAt.clear();
}

// 등급 행 고르기(pickTierRows) — **마이그레이션(20261022100000) 적용 전 · 서버 함수 일시 오류 때 물러나기 전용**. 적용 뒤에는 서버 my_tier_rows 가 같은 규칙으로 골라 준다(60차 #3).
// 규칙은 src/lib/tierRowPick.js 한 곳(데모와 같이 씀 · 66차 #4)

// 내 등급 화면의 이력(월별 전부·일별 기간) — 서버 my_tier_rows 가 서버 규칙으로 골라 준다(60차). 마이그레이션 전이면 옛 질의 + 같은 규칙(pickTierRows).
// 돌려주는 순서는 오래된 것부터(화면 이력·추이 차트) · 오류는 { error }
// 서버 my_tier_rows 한 번 — 요약 카드·내 등급 이력이 같은 규칙을 쓴다(90차 #6 — 두 벌이면 한쪽만 바뀌어 카드와 이력이 어긋난다).
//   { data } 서버가 고른 행 · { fallback: true } 표 읽기로 물러나기(함수 없음 = 마이그레이션 전 · 일시 오류 = 제한 시간·연결·네트워크 · 82차 #3)
//   그 밖의 오류(함수 안의 고장 42883·42P01 등 · 83차 #1)와 앞문 거부(null · 61차 #4)는 throw
async function callMyTierRows(args, label) {
  const { data, error } = await supabase.rpc("my_tier_rows", args);
  if (error) {
    if (!isMissingFunctionError(error)) {
      if (!isTransientRpcError(error)) throw error;
      console.warn(`my_tier_rows(${label}) 일시 오류 — 표 읽기로 물러남:`, error);
    }
    return { fallback: true };
  }
  if (data === null || data === undefined) throw new Error("my_tier_rows: 이 이메일의 등급을 볼 수 없어요(로그인 이메일과 다름)");
  return { data };
}

export async function fetchMyTierHistory(email, dailySince) {
  let res;
  try {
    res = await callMyTierRows({ p_email: email, p_months: null, p_daily_since: dailySince, p_summary: false }, "이력");
  } catch (error) {
    return { monthly: [], daily: [], error };
  }
  if (!res.fallback) {
    const monthly = (Array.isArray(res.data.monthly) ? res.data.monthly : []).slice().reverse();
    const daily = (Array.isArray(res.data.daily) ? res.data.daily : []).slice().reverse();
    return { monthly, daily, error: null };
  }
  try {
    const t = await readTierTables(email, { dailySince });
    return { monthly: t.monthly.reverse(), daily: t.daily.reverse(), error: null };
  } catch (error) {
    return { monthly: [], daily: [], error };
  }
}

// 표 직접 읽기(물러나기 전용) — 요약·이력이 같이 쓴다(90차 #6). 등급 행은 채널 주인 글자 그대로 적힐 수 있어(대소문자만 다른 행) ilike 로 넓게 받고
// pickTierRows 가 정확한 주인·같은 날(달) 고르는 규칙으로 고른다(57차 #2 · 58차 #1 · 87차 #3). ilike 는 색인을 못 타지만 일별 9,219행·월별 496행(2026-10-10 실측)이고
// 본인 행만 보이는 접근 잠금이라 비용이 작다 — 정확성(카드 = 서버 판정)이 먼저. 최신부터 · 오류는 throw
async function readTierTables(email, { monthlySelect = "*", monthlyLimit = null, monthlyKeep = Infinity, dailySelect = "*", dailySince = null, dailyLimit = null, dailyKeep = Infinity } = {}) {
  let mq = supabase.from("creator_tier_monthly").select(monthlySelect).ilike("user_email", ownerLike(email)).order("month", { ascending: false });
  if (monthlyLimit) mq = mq.limit(monthlyLimit);
  let dq = supabase.from("creator_tier_daily").select(dailySelect).ilike("user_email", ownerLike(email));
  if (dailySince) dq = dq.gte("computed_date", dailySince);
  dq = dq.order("computed_date", { ascending: false });
  if (dailyLimit) dq = dq.limit(dailyLimit);
  const [monthlyResult, dailyResult] = await Promise.all([mq, dq]);
  if (monthlyResult.error || dailyResult.error) throw monthlyResult.error || dailyResult.error;
  return {
    monthly: pickTierRows(monthlyResult.data, email, "month", monthlyKeep),
    daily: pickTierRows(dailyResult.data, email, "computed_date", dailyKeep),
  };
}

// 채널 수(삭제 채널 빼고) — 0단계(채널 없음)/1단계(채널 있음) 구분용. 실패하면 null(= 있다고 본다)
// 채널 읽기는 fetchOwnChannels 한 곳(대소문자 섞인 주인 · 정확한 주인만 — 53차 #1 · 54차 #2 · 112차 #4). 삭제 채널은 서버에서 뺀다(지운 채널이 많은 계정도 살아 있는 것만 받는다 · 113차 #7)
async function countOwnChannels(email) {
  const chans = await fetchOwnChannels(email, { liveOnly: true });
  return chans ? chans.length : null;
}

// 채널 수는 보통 서버 my_tier_rows 가 등급 행과 같이 준다(질의 한 번 · 60차). 아래는 마이그레이션 적용 전 물러나기 —
// 루키 여정 화면을 그리는 쪽(includeVariants)만 · 루키인지 본 뒤 필요할 때만 센다(57차 #5 · 59차 #1). 동시에 뜬 화면들은 한 번 센 것을 나눠 쓴다(59차 #3).
// 0개·실패(null)는 캐시에 남기지 않는다 — 채널을 등록하고 돌아오거나 일시 오류가 풀리면 바로 다시 센다(58차 #3 · 59차 #2)
let channelInflight = null; // { email, promise }
function countOwnChannelsShared(email) {
  if (channelInflight && channelInflight.email === email) return channelInflight.promise;
  const promise = countOwnChannels(email)
    .catch(() => null)
    .finally(() => {
      if (channelInflight && channelInflight.promise === promise) channelInflight = null;
    });
  channelInflight = { email, promise };
  return promise;
}

// 캐시는 5분, **채널 0개(「채널 등록하기」 0단계)만 30초**(70차 #4). 루키 카드의 진행도는 이 훅이 아니라 심사 상태(useRookiePromotionStatus)가 그리므로
// 짧은 캐시가 진행도를 빨리 보여 주지 않는다 — 짧게 둘 이유는 0단계뿐이다: 채널을 다른 기기·운영팀 대신 등록 등 이 탭이 모르는 경로로 등록해도
// 화면을 옮기면 30초 안에 「채널 등록하기」가 사라지게. 이 탭에서 채널을 쓰면 src/lib/channelWrites.js(channelWrite)·src/lib/tierWrites.js(tierRpc)가 성공하면
// invalidateTierRowsCache 로 그 자리에서 다시 그리게 한다(65차 #1 · 91차 #5 · 92차 #1). 화면이 직접 부르지 않는다
// 채널 수는 루키·월 행 없는 회원만 받는다(서버 my_tier_rows · 67차 #4) — 정식 등급 회원은 늘 5분
const ROWS_CACHE_TTL_ZERO_CHANNEL_MS = 30 * 1000;
const rowsTtl = (rows) => (rows?.channelCount === 0 ? ROWS_CACHE_TTL_ZERO_CHANNEL_MS : ROWS_CACHE_TTL_MS);
const cacheWarm = (email) => {
  const c = rowsCache.get(email);
  return Boolean(c && Date.now() - c.at < rowsTtl(c.rows));
};

export async function fetchTierRows(email, { needChannels = false } = {}) {
  const rows = await fetchBaseTierRows(email);
  const monthRow = rows.monthlyRows[0] || null;
  const need = needChannels && (!monthRow || monthRow.status === "rookie");
  if (!need) return rows;
  if (rows.channelCount !== undefined) return rows;
  const count = await countOwnChannelsShared(email);
  const next = { ...rows, channelCount: count };
  // 캐시는 새 항목으로 바꾼다 — 이미 다른 화면이 쥔 행 객체를 고치면 그 화면 모르게 값과 캐시 수명(0개 30초)이 바뀐다(76차 #5).
  // 실패(null)는 넣지 않는다(다음에 다시 센다) · 그사이 무효화됐거나 다른 회원 캐시면 넣지 않는다
  const c = rowsCache.get(email);
  if (count !== null && c && c.rows === rows) rowsCache.set(email, { ...c, rows: next });
  return next;
}

// 등급 컷은 본인 최신 월 기준 1행만. 실패해도 전체를 막지 않는다 — 컷이 없으면 "다음 등급까지"만 빠지고 나머지 요약은 그대로 보인다
async function fetchStatsRow(month) {
  const { data: statsData, error: statsError } = await supabase
    .from("creator_tier_stats")
    .select("*")
    .eq("month", month)
    .limit(1);
  if (statsError) {
    console.error("등급 컷(creator_tier_stats) 조회 실패:", statsError);
    return null;
  }
  return (statsData && statsData[0]) || null;
}

async function fetchBaseTierRows(email) {
  if (cacheWarm(email)) return rowsCache.get(email).rows;
  if (rowsInflight.has(email)) return rowsInflight.get(email);

  const gen = tierRowsGeneration();
  noteTierAttempt(rowsLastAttemptAt, rowsWatch, email);
  // 이 조회를 시작한 뒤 무효화됐으면 캐시에 쓰지 않는다(옛 채널 수가 되살아나지 않게 · 64차 #1)
  const remember = (rows) => {
    if (gen === tierRowsGeneration()) {
      const now = Date.now();
      // 수명이 다한 칸 정리 — 지금 그리는 회원의 칸은 남긴다: 지우면 탭 복귀 판정이 그 회원을 「캐시 없음 = 지난 조회 실패」로 읽어
      // 정식 등급 회원도 탭 복귀마다 다시 읽는다(105차 #6). 남긴 칸은 수명이 지나 다음 조회 때 새로 받는다(cacheWarm)
      for (const [e, c] of rowsCache) if (!rowsWatch.isActive(e) && now - c.at >= rowsTtl(c.rows)) rowsCache.delete(e);
      rowsCache.set(email, { at: now, rows });
      if (rowsInflight.get(email) === promise) rowsInflight.delete(email);
    }
    return rows;
  };
  const promise = (async () => {
    // 서버가 고른 내 등급 행·채널 수·마지막 쇼츠 한 번에(my_tier_rows — 규칙 한 곳 · lower 색인 · 60차). 마이그레이션 전이면 아래 옛 질의로
    // 채널 수도 같이 받는다(p_channels 를 넘기지 않음 = 서버 기본 true) — **의도한 비용**이다: 서버는 루키·행 없는 회원만 색인(lower("user"))으로 센다(67차 #4·#5).
    // 배지(채널 수 불필요)가 먼저 캐시를 채워도 나중에 뜨는 루키 여정 화면이 따로 다시 세지 않게(63차 #2 · 73차 #5) — 한 회원 5분(0개면 30초)에 한 번
    // 채널을 쓰면 channelWrite·tierRpc 가 캐시를 비우고(91차 #5 · 92차 #1), 채널 0개는 30초 캐시(rowsTtl · 70차 #4)
    // 서버 함수가 **일시** 오류를 내거나 없으면(마이그레이션 전) 등급 카드를 지우지 않고 아래 표 읽기로 물러난다 — 판정은 callMyTierRows 한 곳(82차 #3 · 83차 #1 · 90차 #6).
    // 앞문이 거부하면(null) 「등급 행 없음」으로 그리지 않고 오류(정식 등급 회원이 「아직 시작 안 함」을 보게 된다 · 61차 #4)
    const res = await callMyTierRows({ p_email: email, p_months: 2 }, "요약");
    if (!res.fallback) {
      const mine = res.data;
      const monthlyRows = Array.isArray(mine?.monthly) ? mine.monthly : [];
      const monthRow = monthlyRows[0] || null;
      const dailyRow = (Array.isArray(mine?.daily) ? mine.daily : [])[0] || null;
      const statsRow = mine.stats ?? null;   // 최신 달의 등급 컷 — 서버가 같이 준다(따로 한 번 더 묻지 않게 · 62차 #4)
      const dormantNow = dailyRow ? Boolean(dailyRow.is_dormant) : Boolean(monthRow && monthRow.is_dormant);
      // 부탁하지 않았으면 undefined(= 아직 안 셈 → 필요한 화면이 나중에 셈) · 숫자가 아니면 null(못 셈)
      // null·빈 값은 Number() 가 0 으로 바꾸므로 먼저 거른다 — 「못 셈」이 「채널 0개」(채널 등록하기)로 읽히지 않게(68차 #1)
      const rawCount = mine.channel_count;
      const channelCount = !("channel_count" in mine) ? undefined
        : rawCount === null || rawCount === "" || !Number.isFinite(Number(rawCount)) ? null : Number(rawCount);
      return remember({ monthlyRows, dailyRow, statsRow, channelCount, lastShortsAt: dormantNow ? mine?.last_shorts_at ?? null : null });
    }
    // ↓ 마이그레이션 적용 전 · 서버 함수 일시 오류 때 물러나기 — 표 읽기 한 곳(readTierTables · 90차 #6)
    const tables = await readTierTables(email, {
      monthlySelect: "user_email,month,band,status,is_dormant,percentile_overall,overall_score,rookie_progress_current,rookie_progress_target,band_rank,band_size,computed_at",
      // 개수 제한은 넉넉히 — 주인 대소문자만 다른 행이 여럿이면 적은 제한이 이길 행이나 두 번째 달을 잘라 낸다(95차 #3). 본인 행만 보여 많지 않다
      monthlyLimit: 200,
      monthlyKeep: 2,
      dailySelect: "user_email,computed_date,band,overall_score,is_dormant,band_rank,band_size,status,rookie_progress_current,rookie_progress_target,computed_at",
      dailyLimit: 200,
      dailyKeep: 1,
    });
    const monthlyRows = tables.monthly;
    const monthRow = monthlyRows[0] || null;
    const dailyRow = tables.daily[0] || null;

    // 등급 컷은 본인 최신 월 기준 1행만. 실패해도 전체를 막지 않는다 — 컷이 없으면
    // "다음 등급까지"만 빠지고 나머지 요약은 그대로 보인다.
    // 등급 컷과 휴면의 마지막 쇼츠는 서로 기다리지 않는다 — 장애 때 물러나기 경로가 질의 넷을 차례로 기다리지 않게(116차 #5)
    const statsPromise = monthRow ? fetchStatsRow(monthRow.month) : Promise.resolve(null);

    // 채널 수는 여기서 세지 않는다 — 필요한 쪽(fetchTierRows needChannels)만 처음 한 번(undefined = 아직 안 셈 · 57차 #5).
    // 삭제한 채널(deleted_at)은 세지 않는다(서버 재계산의 「채널 없음」과 같게) · 루키는 늘 센다 — 「진행도 0일 때만」(53차 #6)은 이 훅의 일별 행이
    // 카드 진행도(get_rookie_promotion_status)와 어긋나면 「채널 등록하기」를 놓쳐 되돌렸다(56차 #1)
    let channelCount;

    // 휴면일 때만 — 마지막 쇼츠 제작일. 일별 is_dormant 를 우선 본다(복귀가 다음 날 반영).
    // 내 채널 목록은 한 번만 받아(삭제 채널 포함) 마지막 쇼츠와 채널 수(삭제 안 한 채널 · 루키·월 행 없는 회원만 — 서버 my_tier_rows 와 같게)가 나눠 쓴다(93차 #6)
    const dormantNow = dailyRow ? Boolean(dailyRow.is_dormant) : Boolean(monthRow && monthRow.is_dormant);
    let lastShortsAt = null;
    const dormantPromise = dormantNow
      ? fetchOwnChannels(email).then(async (own) => ({ own, last: await fetchLastShortsAt(own) }))
      : Promise.resolve(null);
    const [statsRow, dormantFound] = await Promise.all([statsPromise, dormantPromise]);
    if (dormantFound) {
      const { own, last } = dormantFound;
      lastShortsAt = last;
      if (own && (!monthRow || monthRow.status === "rookie")) channelCount = own.filter((c) => !c.deleted_at).length;
    }

    return remember({ monthlyRows, dailyRow, statsRow, channelCount, lastShortsAt });
  })().catch((err) => {
    if (rowsInflight.get(email) === promise) rowsInflight.delete(email);
    throw err;
  });

  rowsInflight.set(email, promise);
  return promise;
}

// 본인 채널(channel."user" = email)의 YouTube 채널 id 로 youtube_video_map 을 찾아 작품 연결
// 영상(licensed_video_id 있음)의 published_at 최댓값. 등급 계산식의 `lastact` 와 같은 재료다 —
// 등급에 세는 영상만(서버 video_counts_for_tier: 표시 없음 · 이유 없음 · 확인 중 · seen_alive). 삭제·비공개 확정 영상까지 보면
// 「영상을 지워 휴면」인데 「마지막 쇼츠 3일 전」으로 어긋난다(리뷰 52차 #2)
export const LAST_SHORTS_COUNTED_FILTER = "dead_at.is.null,dead_reason.is.null,dead_reason.in.(unknown,seen_alive)";
// 내 채널(삭제 채널 포함 — 등급 계산 wv 의 own 과 같은 집합) · 정확한 주인만(54차 #2). 실패하면 null.
// liveOnly — 삭제 채널을 서버에서 뺀다(채널 수 세기 · 113차 #7)
async function fetchOwnChannels(email, { liveOnly = false } = {}) {
  let q = supabase.from("channel").select("id, channel_id, user, deleted_at").ilike("user", ownerLike(email));
  if (liveOnly) q = q.is("deleted_at", null);
  const { data: chRows, error: chErr } = await q;
  if (chErr) {
    console.error("채널 조회 실패:", chErr);
    return null;
  }
  return (chRows || []).filter((c) => sameOwnerEmail(c.user, email));
}

async function fetchLastShortsAt(chans) {
  if (!chans || chans.length === 0) return null;
  const ids = chans.map((c) => c.channel_id).filter(Boolean);
  if (ids.length === 0) return null;
  const query = (counted) => {
    let q = supabase
      .from("youtube_video_map")
      .select("published_at")
      .in("channel_id", ids)
      .not("licensed_video_id", "is", null);
    if (counted) q = q.or(LAST_SHORTS_COUNTED_FILTER);
    // 게시 시각이 빈 행은 뺀다 — 내림차순에서 빈 값이 맨 앞이라 「마지막 쇼츠 없음」이 됐다(서버 max() 는 빈 값을 건너뛴다 · 114차 #2)
    return q.not("published_at", "is", null).order("published_at", { ascending: false }).limit(1);
  };
  let { data, error } = await query(true);
  // 마이그레이션(20261022100000) 전이면 dead_reason 칸이 없다 — 예전 질의로(화면 배포가 DB 적용보다 먼저일 수 있다)
  // 「칸 없음」일 때만 — 필터 문법 오류 등 다른 오류에 물러나면 지운 영상까지 세는 옛 질의가 조용히 돈다(53차 #2)
  if (isMissingColumnError(error)) ({ data, error } = await query(false));
  if (error) {
    console.error("마지막 쇼츠 조회 실패:", error);
    return null;
  }
  return (data && data[0] && data[0].published_at) || null;
}

function toNum(v) {
  return v === null || v === undefined ? null : Number(v);
}
function fmtGap(n) {
  return n > 0 ? n.toFixed(1) : null;
}

// 원본 행 → 화면이 쓰는 요약. 컷 수치는 여기서만 쓰고 밖으로 내보내지 않는다(노출 금지 대상).
// 순수 함수 — 테스트에서 직접 부른다.
// 시각을 받지 않는다 — 날짜(다음 결정일 등)는 요약에 굳히지 않고 화면이 그리는 때 계산한다(132차 #4 · 133차 #2)
export function deriveSummary(rows, includeVariants) {
  const { monthlyRows, dailyRow, statsRow, channelCount, lastShortsAt } = rows;
  const monthRow = monthlyRows[0] || null;

  // 등급 행이 없는 승인 크리에이터 — "아직 시작 안 함". includeVariants 일 때만.
  // 2026-10-06 — 월 확정 행은 매월 1일에만 생기므로, 달 중간에 채널·신청·영상을 시작한 회원은
  // 다음 1일까지 월 행이 없다. 그동안 진행도는 **일별 행**(매일 07:00 계산)이 정본이다 — 이성재(올더비즈)
  // 사례: 10/4~5 에 10편을 올려 일별 행은 5/5 인데 화면은 0/5 고정. 상태(pre-rookie)·청구 판정은 그대로 두고
  // 진행도·계산일만 일별 행에서 가져온다. 휴면은 보지 않는다(영상이 아예 없는 새 계정이 "휴면"으로 보이면 안 된다).
  if (!monthRow) {
    if (!includeVariants) return null;
    // 일별 행이 루키일 때만 진행도로 — 정식(normal · 띠 있음) 일별 행은 목표를 채운 것으로 본다(119차 #3 · 월 확정 루키 갈래 118차 #2 와 같게).
    // 정식 일별 행이 곧 「승인됨」은 아니다(기존 회원·심사 제도 꺼짐도 정식 행을 받는다 · 120차 #1) — 승인 여부는 심사 상태(rookieReview)만 말한다.
    // 그 행의 띠는 formalDailyTier 로만 넘긴다(「승급 승인」 카드의 「오늘 기준」 줄 · 120차 #4). dailyTier 에 넣지 않는다 — 요금 예고(tierPricing forecastTier)·
    // 등급 트랙이 dailyTier 를 읽어, 월 행 없는 회원에게 금액·등급 표시가 새로 생길 수 있다
    const dailyFormal = Boolean(dailyRow && dailyRow.status === "normal" && dailyRow.band);
    const dailyProgress = dailyRow && !dailyFormal
      ? { current: dailyRow.rookie_progress_current ?? 0, target: dailyRow.rookie_progress_target ?? 5 }
      : dailyFormal
        ? { current: dailyRow.rookie_progress_current ?? dailyRow.rookie_progress_target ?? 5, target: dailyRow.rookie_progress_target ?? 5 }   // 적힌 진행도 그대로(122차 #7)
        : { current: 0, target: 5 };
    return {
      confirmedTier: null,
      confirmedMonth: null,
      dailyTier: null,
      formalDailyTier: dailyFormal ? tierNameFromBand(dailyRow.band) : null,
      dailyDate: dailyRow ? dailyRow.computed_date : null,
      primaryTier: null,
      percentileOverall: null,
      status: "pre-rookie",
      isDormant: false,
      nextTier: null,
      gapToNext: null,
      gapToKeep: null,
      direction: null,
      progressPercent: null,
      rookieProgress: dailyProgress,
      promotedFrom: null,
      demotedFrom: null,
      debuted: false,
      bandRank: null,
      bandSize: null,
      lastShortsAt: null,
      // 못 셌으면(null) 있다고 본다 — 루키 갈래와 같은 규칙(조회 실패로 「채널 등록하기」가 뜨지 않게 · 리뷰 52차 #4)
      hasChannels: channelCount !== 0,
    };
  }

  // 휴면은 일별 행이 있으면 그쪽을 믿는다 — 복귀가 다음 날 바로 반영되게.
  const isDormant = dailyRow ? Boolean(dailyRow.is_dormant) : Boolean(monthRow.is_dormant);
  const isRookie = monthRow.status === "rookie";

  // 기존 계약(2026-08-30): 정식 등급 전제 호출부는 루키·휴면·등급 미보유에서 카드를 그리지 않는다.
  if (!includeVariants) {
    if (monthRow.status !== "normal" || isDormant || !monthRow.band) return null;
  } else if (!monthRow.band && !isRookie) {
    return null;
  }

  const confirmedTier = monthRow.band ? tierNameFromBand(monthRow.band) : null;
  const dailyTier = dailyRow && dailyRow.band ? tierNameFromBand(dailyRow.band) : null;
  const primaryTier = dailyTier || confirmedTier;

  const percentileOverall =
    monthRow.percentile_overall === null || monthRow.percentile_overall === undefined
      ? null
      : Number(monthRow.percentile_overall).toFixed(1);

  // 2026-10-26 만족도 제외 — 점수는 70점 눈금(displayOverallScore: 2026-10-27 전 기록은 만족도 몫을 뺀다),
  // 컷도 같은 눈금(cutForTierOnScoreScale). 옛 통계 행(100점 눈금)과 새 점수를 섞으면 「다음 등급까지」가 29점쯤 어긋난다.
  const overallScore = displayOverallScore(dailyRow) ?? displayOverallScore(monthRow);
  const cutFor = (name) => cutForTierOnScoreScale(name, [statsRow], dailyRow || monthRow); // 「자료 방식」 기록이면 그 컷

  // 방향 — 오늘 등급 vs 적용 등급. 둘 다 있을 때만.
  let direction = null;
  if (confirmedTier && dailyTier) {
    const d = tierIndex(dailyTier) - tierIndex(confirmedTier);
    direction = d > 0 ? "up" : d < 0 ? "down" : "same";
  }

  // 다음 등급까지 / 유지·여유 — 휴면·루키는 목표를 두지 않는다.
  let nextTier = null;
  let gapToNext = null;
  let gapToKeep = null;
  let progressPercent = null;
  if (!isDormant && !isRookie && primaryTier) {
    nextTier = nextTierName(primaryTier);
    const cutNext = nextTier ? cutFor(nextTier) : null;
    const cutCurrent = cutFor(primaryTier);
    if (cutNext !== null && overallScore !== null) {
      gapToNext = fmtGap(cutNext - overallScore);
      if (cutCurrent !== null && cutNext > cutCurrent) {
        const ratio = (overallScore - cutCurrent) / (cutNext - cutCurrent);
        progressPercent = Math.max(0, Math.min(100, Math.round(ratio * 100)));
      }
    } else if (!nextTier && overallScore !== null) {
      progressPercent = 100; // 최고 등급 — 트랙을 끝까지 채운다
    }

    if (overallScore !== null) {
      if (direction === "down" && confirmedTier) {
        // 적용 등급을 지키려면 그 컷까지 얼마나 모자란가
        const cutConfirmed = cutFor(confirmedTier);
        if (cutConfirmed !== null) gapToKeep = fmtGap(cutConfirmed - overallScore);
      } else if (direction === "up" || !nextTier) {
        // 오늘 등급(또는 마스터)의 컷 위로 얼마나 여유가 있나
        const cutHold = cutFor(primaryTier);
        if (cutHold !== null) gapToKeep = fmtGap(overallScore - cutHold);
      }
    }
  }

  // 진행도는 일별 행이 있으면 그 값 — 월 확정 행은 1일에 얼어 있어 달 중간에 영상을 지워 내려간 진행도(이 PR 의 핵심)를 못 본다.
  // 심사 상태(get_rookie_promotion_status)가 실패·로딩 중일 때도 「심사 중」 대신 실제 진행도로 그린다(117차 #2 · 월 행 없는 회원과 같게)
  //   일별 행도 루키일 때만 — 승인으로 실버(normal)가 써진 일별 행의 5/5 를 「5/5 루키」로 읽으면 심사 상태 로딩 중에 「승급 심사 중」이 뜬다(118차 #2)
  const dailyHasProgress = Boolean(dailyRow) && dailyRow.status === "rookie"
    && dailyRow.rookie_progress_current !== null && dailyRow.rookie_progress_current !== undefined;
  //   일별 행이 정식(normal · 띠 있음)이면 그 행의 진행도 — 1일에 얼은 월 행 값(3/5)으로 「2편 더」를 말하지 않게(121차 #1). 그 행에 적힌 진행도를 그대로 쓰고
  //   (5/6 을 6/6 으로 덮지 않는다 · 122차 #7), 진행도 칸이 비었을 때만 목표를 채운 것으로 본다(월 행 없는 회원 갈래와 같은 규칙)
  const dailyFormalRow = Boolean(dailyRow && dailyRow.status === "normal" && dailyRow.band);
  const formalTarget = dailyFormalRow ? dailyRow.rookie_progress_target ?? monthRow.rookie_progress_target ?? 5 : null;
  const rookieProgress = isRookie
    ? dailyHasProgress
      ? { current: dailyRow.rookie_progress_current, target: dailyRow.rookie_progress_target ?? 5 }
      : dailyFormalRow
        ? { current: dailyRow.rookie_progress_current ?? formalTarget, target: formalTarget }
        : { current: monthRow.rookie_progress_current ?? 0, target: monthRow.rookie_progress_target ?? 5 }
    : null;

  // 직전 달과 비교 — 승급 / 강등 / 데뷔(루키 → 정식). 표시 기간(1~7일)은 뷰모델이 판단한다.
  let promotedFrom = null;
  let demotedFrom = null;
  let debuted = false;
  const prevRow = monthlyRows[1] || null;
  if (confirmedTier && prevRow) {
    if (prevRow.band) {
      const prevTier = tierNameFromBand(prevRow.band);
      if (prevTier) {
        const d = tierIndex(confirmedTier) - tierIndex(prevTier);
        if (d > 0) promotedFrom = prevTier;
        else if (d < 0) demotedFrom = prevTier;
      }
    } else if (prevRow.status === "rookie" && monthRow.status === "normal") {
      debuted = true;
    }
  }

  const rankRow = dailyRow && dailyRow.band_rank ? dailyRow : monthRow;

  return {
    confirmedTier,
    confirmedMonth: monthRow.month,
    dailyTier,
    dailyDate: dailyRow ? dailyRow.computed_date : null,
    primaryTier,
    percentileOverall,
    status: isRookie ? "rookie" : "normal",
    isDormant,
    nextTier,
    gapToNext,
    gapToKeep,
    direction,
    progressPercent,
    rookieProgress,
    // 오늘 일별 행이 이미 정식이면 그 띠 — 심사 상태를 못 읽었을 때 카드가 「오늘 기준 ○○」만 말하게(123차 #1 · 월 행 없는 회원 갈래와 같은 칸)
    formalDailyTier: isRookie && dailyFormalRow ? tierNameFromBand(dailyRow.band) : null,
    promotedFrom,
    demotedFrom,
    debuted,
    bandRank: toNum(rankRow.band_rank),
    bandSize: toNum(rankRow.band_size),
    lastShortsAt: isDormant ? lastShortsAt || null : null,
    // 루키만 채널 수를 센다(위 질의) — 0 이면 「채널 등록부터」. 못 셌으면(null) 예전처럼 있다고 본다
    hasChannels: channelCount !== 0,
  };
}

export default function useCreatorTierSummary(email, { enabled = true, includeVariants = false } = {}) {
  const [loading, setLoading] = useState(Boolean(enabled && email));
  const [summary, setSummary] = useState(null);
  // 무효화 세대 — 채널 등록·삭제로 캐시가 비면 떠 있는 화면도 다시 조회한다(65차 #1)
  const [generation, setGeneration] = useState(tierRowsGeneration);
  const lastKeyRef = useRef(null);

  // 신호는 켜진 훅만 듣는다 — 등급이 없는 역할(운영·권리사·파트너)의 꺼진 훅이 채널 쓰기·탭 복귀마다 다시 그려지지 않게(95차 #2 · 심사 상태 훅과 같게 94차 #3)
  const active = Boolean(enabled && email);
  useEffect(() => (active ? rowsWatch.watch(email, setGeneration) : undefined), [active, email]);

  useEffect(() => {
    if (!enabled || !email) {
      lastKeyRef.current = null;
      setSummary(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    // 같은 회원·같은 화면에서 세대만 바뀐 다시 조회는 조용히 — 이미 그린 카드·배지를 로딩으로 지우지 않는다
    const key = `${email}|${includeVariants}`;
    const silent = lastKeyRef.current === key;
    lastKeyRef.current = key;

    async function load() {
      if (!silent) setLoading(true);
      try {
        const rows = await fetchTierRows(email, { needChannels: includeVariants });
        if (cancelled) return;
        setSummary(deriveSummary(rows, includeVariants));
      } catch (err) {
        if (cancelled) return;
        console.error("등급 요약 조회 실패:", err);
        if (!silent) setSummary(null);   // 조용한 다시 조회가 실패하면 그려 둔 값을 지우지 않는다
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [email, enabled, includeVariants, generation]);

  return { loading, summary };
}
